import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";
import { textTruncate } from "src/helpers/truncateText";
import { useSearchParams } from "react-router-dom";

export const ScreeningAlertContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = {
  LOW: "#05CD99",
  MEDIUM: "#F8961E",
  HIGH: "#F94144",
};

const ScreeningAlertProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const pageQuery = queryParams.get("page");

  const [searchParam, setSearchParam] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["screening_alert", searchValue, pageQuery],
    // staleTime: 30000,
    queryFn: async () => {
      const url = searchValue
        ? `onboarding/risk-profiles?search=${searchValue}&page_size=10&page=1`
        : `onboarding/risk-profiles?page_size=10&page=${
            Number(pageQuery) - 1 + 1
          }`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          first_name: string;
          last_name: string;
          other_name: string;
          source: string;
          created_at: string;
          email: string;
          risk_status: string;
          risk_score: number;
          business_name: string;
        }) => ({
          id: res.id,
          "customer name": `${
            !res.first_name?.toLowerCase() &&
            !res.last_name?.toLowerCase() &&
            !res.other_name?.toLowerCase() &&
            !res.business_name?.toLowerCase()
              ? "N/A"
              : res.business_name &&
                  !res.first_name?.toLowerCase() &&
                  !res.last_name?.toLowerCase() &&
                  !res.other_name?.toLowerCase()
                ? textTruncate(res.business_name, 20)
                : textTruncate(
                    `${res.first_name?.toLowerCase() || ""} ${
                      res.other_name?.toLowerCase() || ""
                    } ${res.last_name?.toLowerCase() || ""}`,
                    20,
                  )
          }`,
          date: format(new Date(res.created_at), "dd/MM/yyyy"),
          "email address": res.email?.toLowerCase() || "N/A",
          risk: res.risk_status,
          "risk intensity": (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  background: "#D9DDE3",
                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    background: score_color[res.risk_status],
                    height: "1.2vh",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    width: `${res.risk_score}%`,
                  }}
                />
              </div>
              {res.risk_score.toFixed(2)}%
            </div>
          ),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearchRecord,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
    ],
  );
  return (
    <ScreeningAlertContext.Provider value={contextValue}>
      {children}
    </ScreeningAlertContext.Provider>
  );
};

export default ScreeningAlertProvider;
